import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_URL from '../../config';
import { Button } from '../ui';

const PAGE_LIMIT = 50;

function authHeaders(extra = {}) {
  const token = localStorage.getItem('token');
  return {
    headers: { Authorization: token ? `Bearer ${token}` : '' },
    ...extra,
  };
}

function formatJoined(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const match = /filename="([^"]+)"/i.exec(header);
  return match ? match[1] : fallback;
}

export default function DrugTestingWaitlist() {
  const [page, setPage] = useState(1);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchPage = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/fc/drug-testing-waitlist`,
        {
          ...authHeaders(),
          params: { page, limit: PAGE_LIMIT },
        },
      );
      setPayload(data);
    } catch {
      setPayload(null);
      setError('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const handleExportCsv = async () => {
    setExporting(true);
    setError('');
    try {
      const response = await axios.get(
        `${API_URL}/api/fc/drug-testing-waitlist/export`,
        authHeaders({ responseType: 'blob' }),
      );
      const fallback = `drug-testing-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      const filename = filenameFromDisposition(
        response.headers['content-disposition'],
        fallback,
      );
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const total = payload?.total ?? 0;
  const limit = payload?.limit ?? PAGE_LIMIT;
  const pages = Math.max(1, Math.ceil(total / limit) || 1);
  const entries = payload?.entries || [];
  const driverCounts = payload?.driverCountCounts || {};
  const interestCounts = payload?.interestCounts || {};

  return (
    <section className="founder-admin__waitlist" style={{ marginBottom: 32 }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <h2 className="founder-admin__section-title">Drug-testing waitlist</h2>
          <p className="founder-admin__section-sub">
            {loading && !payload ? 'Landing-v2 signups' : `${total} signup${total === 1 ? '' : 's'}`}
          </p>
        </div>
        <Button variant="secondary" onClick={handleExportCsv} disabled={exporting || loading}>
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      {error ? (
        <div className="founder-admin__error" role="alert">
          {error}
        </div>
      ) : null}

      {loading && !payload ? (
        <p className="founder-admin__muted">Loading…</p>
      ) : error && !payload ? null : (
        <>
          <div className="founder-admin__grid-3" style={{ marginBottom: 16 }}>
            {Object.entries(driverCounts).map(([label, count]) => (
              <div key={label} className="founder-admin__card">
                <div className="founder-admin__stat-num">{count}</div>
                <div className="founder-admin__stat-sub">{label}</div>
              </div>
            ))}
          </div>
          <div className="founder-admin__card" style={{ marginBottom: 16 }}>
            <h3 className="founder-admin__section-title" style={{ fontSize: '1rem' }}>
              What would help most
            </h3>
            <ul className="founder-admin__gap-list">
              {Object.entries(interestCounts).map(([label, count]) => (
                <li key={label}>
                  {label} — {count}
                </li>
              ))}
            </ul>
          </div>

          <div className="founder-admin__table-wrap">
            <table className="founder-admin__table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Drivers</th>
                  <th>Interest</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="founder-admin__muted">
                      No signups yet
                    </td>
                  </tr>
                ) : (
                  entries.map((row) => (
                    <tr key={row.id}>
                      <td>{row.email}</td>
                      <td>{row.driverCount}</td>
                      <td>{row.interest || '—'}</td>
                      <td>{formatJoined(row.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="founder-admin__pagination">
            <span>
              Page {payload?.page ?? page} of {pages} ({total} signups)
            </span>
            <Button variant="secondary" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={page >= pages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
